import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiMap, FiTarget, FiAward } = FiIcons;

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      icon: FiSearch,
      title: "Brain Scan (Not the Medical Kind)",
      description: "We assess which executive functions are sabotaging your sleep. Is it attention that won't shut off? Working memory overload? Emotional dysregulation?",
      color: "from-accent-magenta to-accent-purple"
    },
    {
      number: "02",
      icon: FiMap,
      title: "Your Personal Sleep-EF Blueprint",
      description: "Get a customized training plan targeting your specific cognitive sleep disruptors. No generic advice—this is precision brain training.",
      color: "from-accent-gold to-accent-yellow"
    },
    {
      number: "03",
      icon: FiTarget,
      title: "Skill Stacking Sessions",
      description: "Weekly 1-on-1 coaching to build and strengthen your EF toolkit. Practice real scenarios, troubleshoot challenges, level up your brain's sleep game.",
      color: "from-accent-lavender to-accent-purple"
    },
    {
      number: "04",
      icon: FiAward,
      title: "Sleep Like a Pro",
      description: "We ensure these strategies become automatic, building resilience that goes far beyond sleep.",
      color: "from-green-400 to-emerald-500"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-8">
            From Sleep Wreck to Sleep Pro in 4 Steps
          </h2>
        </motion.div>

        <div className="space-y-16">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`grid lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
              }`}
            >
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`text-6xl font-bold font-playfair bg-gradient-to-r ${step.color} bg-clip-text text-transparent`}>
                    {step.number}
                  </div>
                  <div className={`p-4 rounded-full bg-gradient-to-r ${step.color} text-white`}>
                    <SafeIcon icon={step.icon} className="w-8 h-8" />
                  </div>
                </div>
                
                <h3 className="text-2xl lg:text-3xl font-playfair font-bold text-gray-900 mb-6">
                  {step.title}
                </h3>
                
                <p className="text-lg text-gray-600 font-raleway leading-relaxed">
                  {step.description}
                </p>
              </div>

              {/* Visual Element */}
              <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                <div className={`relative bg-gradient-to-br ${step.color} rounded-3xl p-12 text-white shadow-2xl`}>
                  <div className="text-center">
                    <SafeIcon icon={step.icon} className="w-20 h-20 mx-auto mb-6 opacity-80" />
                    <div className="text-4xl font-bold font-playfair mb-2">Step {step.number}</div>
                    <div className="text-xl font-semibold opacity-90">{step.title}</div>
                  </div>
                  
                  {/* Decorative elements */}
                  <div className="absolute top-4 right-4 w-16 h-16 bg-white/10 rounded-full"></div>
                  <div className="absolute bottom-4 left-4 w-8 h-8 bg-white/10 rounded-full"></div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Connecting Line */}
        <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-accent-magenta via-accent-gold to-accent-lavender opacity-20"></div>
      </div>
    </section>
  );
};

export default HowItWorks;